/**
 * Alert Service - Handles all alert-related operations
 * Provides methods for fetching, updating, and subscribing to alerts
 */

import { supabase } from '../lib/supabase';

// Alert priority mapping
export const PRIORITY_LABELS = {
  1: 'low',
  2: 'medium',
  3: 'high',
  4: 'critical'
};

export const PRIORITY_VALUES = {
  low: 1,
  medium: 2,
  high: 3,
  critical: 4
};

// Alert type icons and colors
export const ALERT_TYPE_CONFIG = {
  profile_view: {
    icon: 'eye',
    color: 'blue',
    label: 'Profile View'
  },
  interest_spike: {
    icon: 'trending-up',
    color: 'green',
    label: 'Interest Spike'
  },
  new_signal: {
    icon: 'radio',
    color: 'purple',
    label: 'New Signal'
  },
  match_score: {
    icon: 'target',
    color: 'orange',
    label: 'Match Score'
  },
  meeting_request: {
    icon: 'calendar',
    color: 'pink',
    label: 'Meeting Request'
  },
  bd_score_increase: {
    icon: 'arrow-up',
    color: 'green',
    label: 'BD Score Increase'
  },
  bd_score_decrease: {
    icon: 'arrow-down',
    color: 'red',
    label: 'BD Score Decrease'
  },
  executive_change: {
    icon: 'user',
    color: 'yellow',
    label: 'Executive Change'
  },
  patent_cliff: {
    icon: 'alert-triangle',
    color: 'red',
    label: 'Patent Cliff'
  },
  ma_news: {
    icon: 'briefcase',
    color: 'indigo',
    label: 'M&A News'
  }
};

const alertService = {
  /**
   * Get alerts with optional filters
   * @param {Object} options - Query options
   * @param {boolean} options.unreadOnly - Only return unread alerts
   * @param {number} options.limit - Max alerts to return
   * @param {number} options.offset - Offset for pagination
   * @param {string} options.alertType - Filter by alert type
   * @param {number} options.minPriority - Minimum priority level
   * @returns {Promise<{alerts: Array, count: number}>}
   */
  async getAlerts({
    unreadOnly = false,
    limit = 50,
    offset = 0,
    alertType = null,
    minPriority = null
  } = {}) {
    try {
      let query = supabase
        .from('alerts')
        .select(`
          *,
          buyers:buyer_id(id, company_name, stock_ticker)
        `, { count: 'exact' })
        .eq('dismissed', false)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (unreadOnly) {
        query = query.eq('is_read', false);
      }

      if (alertType) {
        query = query.eq('alert_type', alertType);
      }

      if (minPriority) {
        query = query.gte('priority', minPriority);
      }

      const { data, error, count } = await query;

      if (error) throw error;

      // Transform data for easier use
      const alerts = (data || []).map(alert => ({
        ...alert,
        priorityLabel: PRIORITY_LABELS[alert.priority] || 'medium',
        typeConfig: ALERT_TYPE_CONFIG[alert.alert_type] || {
          icon: 'bell',
          color: 'gray',
          label: alert.alert_type
        },
        buyerName: alert.buyers?.company_name,
        buyerTicker: alert.buyers?.stock_ticker
      }));

      return { alerts, count };
    } catch (error) {
      console.error('Error fetching alerts:', error);
      throw error;
    }
  },

  /**
   * Get unread alert count
   * @returns {Promise<number>}
   */
  async getUnreadCount() {
    try {
      const { count, error } = await supabase
        .from('alerts')
        .select('*', { count: 'exact', head: true })
        .eq('is_read', false)
        .eq('dismissed', false);

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  },

  /**
   * Mark a single alert as read
   * @param {string} alertId - Alert ID
   * @returns {Promise<boolean>}
   */
  async markAsRead(alertId) {
    try {
      const { error } = await supabase
        .from('alerts')
        .update({
          is_read: true,
          read_at: new Date().toISOString()
        })
        .eq('id', alertId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error marking alert as read:', error);
      throw error;
    }
  },

  /**
   * Mark all alerts as read
   * @returns {Promise<number>} Number of alerts updated
   */
  async markAllAsRead() {
    try {
      const { data, error } = await supabase
        .from('alerts')
        .update({
          is_read: true,
          read_at: new Date().toISOString()
        })
        .eq('is_read', false)
        .eq('dismissed', false)
        .select();

      if (error) throw error;
      return data?.length || 0;
    } catch (error) {
      console.error('Error marking all alerts as read:', error);
      throw error;
    }
  },

  /**
   * Dismiss an alert (soft delete)
   * @param {string} alertId - Alert ID
   * @returns {Promise<boolean>}
   */
  async dismissAlert(alertId) {
    try {
      const { error } = await supabase
        .from('alerts')
        .update({
          dismissed: true,
          dismissed_at: new Date().toISOString()
        })
        .eq('id', alertId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error dismissing alert:', error);
      throw error;
    }
  },

  /**
   * Track alert click - marks alert as clicked and read
   * Uses the mark_alert_clicked database function
   * @param {string} alertId - Alert ID
   * @returns {Promise<boolean>}
   */
  async trackClick(alertId) {
    try {
      const { error } = await supabase.rpc('mark_alert_clicked', {
        p_alert_id: alertId
      });

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error tracking alert click:', error);
      throw error;
    }
  },

  /**
   * Mark multiple alerts as delivered (batch operation)
   * Uses the mark_alerts_delivered database function
   * @param {string[]} alertIds - Array of alert IDs
   * @returns {Promise<number>} Number of alerts marked
   */
  async markAlertsDelivered(alertIds) {
    try {
      const { data, error } = await supabase.rpc('mark_alerts_delivered', {
        p_alert_ids: alertIds
      });

      if (error) throw error;
      return data || 0;
    } catch (error) {
      console.error('Error marking alerts as delivered:', error);
      throw error;
    }
  },

  /**
   * Get user engagement metrics
   * Uses the get_user_alert_engagement database function
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Engagement metrics
   */
  async getEngagementMetrics(userId) {
    try {
      const { data, error } = await supabase.rpc('get_user_alert_engagement', {
        p_user_id: userId
      });

      if (error) throw error;
      return data || {
        total_alerts: 0,
        unread_count: 0,
        read_count: 0,
        clicked_count: 0,
        dismissed_count: 0,
        read_rate: 0,
        click_rate: 0,
        alerts_today: 0,
        alerts_this_week: 0,
        high_priority_unread: 0
      };
    } catch (error) {
      console.error('Error getting engagement metrics:', error);
      throw error;
    }
  },

  /**
   * Get user's alert preferences
   * @param {string} userId - User ID (optional, uses current user if not provided)
   * @returns {Promise<Object>}
   */
  async getPreferences(userId = null) {
    try {
      // Get current user if userId not provided
      let targetUserId = userId;
      if (!targetUserId) {
        const { data: { user } } = await supabase.auth.getUser();
        targetUserId = user?.id;
      }

      if (!targetUserId) {
        return this.getDefaultPreferences();
      }

      const { data, error } = await supabase
        .from('alert_preferences')
        .select('*')
        .eq('user_id', targetUserId)
        .single();

      if (error) {
        // If no preferences exist, return defaults
        if (error.code === 'PGRST116') {
          return this.getDefaultPreferences();
        }
        throw error;
      }

      return data || this.getDefaultPreferences();
    } catch (error) {
      console.error('Error fetching preferences:', error);
      return this.getDefaultPreferences();
    }
  },

  /**
   * Get default alert preferences
   * @returns {Object}
   */
  getDefaultPreferences() {
    return {
      profile_view_alerts: true,
      interest_spike_alerts: true,
      new_signal_alerts: true,
      match_score_alerts: true,
      meeting_request_alerts: true,
      profile_view_threshold: 3,
      interest_spike_threshold: 20,
      signal_strength_threshold: 7,
      match_score_threshold: 70,
      in_app_notifications: true,
      email_notifications: false,
      email_address: null,
      email_digest_frequency: 'daily',
      quiet_hours_enabled: false,
      quiet_hours_start: '22:00',
      quiet_hours_end: '08:00',
      timezone: 'Asia/Shanghai',
      max_alerts_per_day: 50,
      min_alert_interval_minutes: 30
    };
  },

  /**
   * Update alert preferences
   * @param {Object} preferences - Preferences to update
   * @param {string} userId - User ID (optional, uses current user if not provided)
   * @returns {Promise<Object>}
   */
  async updatePreferences(preferences, userId = null) {
    try {
      // Get current user if userId not provided
      let targetUserId = userId;
      if (!targetUserId) {
        const { data: { user } } = await supabase.auth.getUser();
        targetUserId = user?.id;
      }

      if (!targetUserId) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('alert_preferences')
        .upsert({
          user_id: targetUserId,
          ...preferences,
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating preferences:', error);
      throw error;
    }
  },

  /**
   * Check if user is in quiet hours
   * @param {string} userId - User ID (optional)
   * @returns {Promise<boolean>}
   */
  async isInQuietHours(userId = null) {
    try {
      let targetUserId = userId;
      if (!targetUserId) {
        const { data: { user } } = await supabase.auth.getUser();
        targetUserId = user?.id;
      }

      if (!targetUserId) return false;

      const { data, error } = await supabase.rpc('is_in_quiet_hours', {
        p_user_id: targetUserId
      });

      if (error) throw error;
      return data || false;
    } catch (error) {
      console.error('Error checking quiet hours:', error);
      return false;
    }
  },

  /**
   * Subscribe to real-time alert updates
   * @param {Function} callback - Called when new alert arrives
   * @returns {Object} Subscription object with unsubscribe method
   */
  subscribeToAlerts(callback) {
    const subscription = supabase
      .channel('alerts-channel')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'alerts'
        },
        (payload) => {
          const alert = {
            ...payload.new,
            priorityLabel: PRIORITY_LABELS[payload.new.priority] || 'medium',
            typeConfig: ALERT_TYPE_CONFIG[payload.new.alert_type] || {
              icon: 'bell',
              color: 'gray',
              label: payload.new.alert_type
            }
          };
          callback(alert);
        }
      )
      .subscribe();

    return {
      unsubscribe: () => subscription.unsubscribe()
    };
  },

  /**
   * Get recent alerts for a specific buyer
   * @param {string} buyerId - Buyer ID
   * @param {number} limit - Max alerts to return
   * @returns {Promise<Array>}
   */
  async getAlertsByBuyer(buyerId, limit = 10) {
    try {
      const { data, error } = await supabase
        .from('alerts')
        .select('*')
        .eq('buyer_id', buyerId)
        .eq('dismissed', false)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching buyer alerts:', error);
      throw error;
    }
  },

  /**
   * Get alert statistics
   * @returns {Promise<Object>}
   */
  async getStatistics() {
    try {
      const { data: alerts, error } = await supabase
        .from('alerts')
        .select('alert_type, priority, is_read, created_at, clicked, dismissed')
        .eq('dismissed', false)
        .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());

      if (error) throw error;

      const stats = {
        total: alerts.length,
        unread: alerts.filter(a => !a.is_read).length,
        read: alerts.filter(a => a.is_read).length,
        clicked: alerts.filter(a => a.clicked).length,
        highPriority: alerts.filter(a => a.priority >= 3).length,
        today: alerts.filter(a =>
          new Date(a.created_at) > new Date(Date.now() - 24 * 60 * 60 * 1000)
        ).length,
        thisWeek: alerts.filter(a =>
          new Date(a.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
        ).length,
        byType: {},
        byPriority: { low: 0, medium: 0, high: 0, critical: 0 }
      };

      alerts.forEach(alert => {
        // Count by type
        stats.byType[alert.alert_type] = (stats.byType[alert.alert_type] || 0) + 1;

        // Count by priority
        const label = PRIORITY_LABELS[alert.priority] || 'medium';
        stats.byPriority[label]++;
      });

      return stats;
    } catch (error) {
      console.error('Error fetching statistics:', error);
      throw error;
    }
  },

  /**
   * Create a test alert (for development/testing)
   * @param {Object} alertData - Alert data
   * @returns {Promise<Object>}
   */
  async createTestAlert(alertData = {}) {
    try {
      const defaultAlert = {
        alert_type: 'profile_view',
        title: 'Test Alert',
        description: 'This is a test alert for development',
        priority: 2,
        is_read: false,
        dismissed: false,
        delivered: false,
        in_app_sent: false,
        email_sent: false,
        clicked: false,
        data: { test: true },
        created_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('alerts')
        .insert({ ...defaultAlert, ...alertData })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating test alert:', error);
      throw error;
    }
  }
};

export default alertService;
